import * as Sentry from '@sentry/vue'
import type { App } from 'vue'
import type { Router } from 'vue-router'
import { SENTRY_DSN, ENVIRONMENT, SENTRY_RELEASE } from '@/config/env'

/**
 * Inicializa Sentry para el dashboard de FlowEstudio.
 *
 * - No se inicializa si VITE_SENTRY_DSN está vacío.
 * - No se inicializa en entorno development.
 * - No envía eventos desde localhost aunque el DSN esté configurado.
 *
 * Llamar en src/main.ts después de registrar pinia y router, antes de app.mount().
 */
export function initSentry(app: App, router: Router): void {
  if (!SENTRY_DSN || ENVIRONMENT === 'development') return

  Sentry.init({
    app,
    dsn: SENTRY_DSN,
    environment: ENVIRONMENT,
    ...(SENTRY_RELEASE ? { release: SENTRY_RELEASE } : {}),

    integrations: [
      // Registra cada cambio de ruta como una transacción de performance
      Sentry.browserTracingIntegration({ router }),
      // Graba la sesión del usuario cuando ocurre un error
      Sentry.replayIntegration({
        maskAllText: true,    // enmascara texto para privacidad
        blockAllMedia: false,
      }),
    ],

    // Performance: 10% de transacciones en producción, 0 en el resto
    tracesSampleRate: ENVIRONMENT === 'production' ? 0.1 : 0,

    // Replay: solo captura sesiones completas ante errores (nunca grabación normal)
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,

    // Errores comunes de browser que no son accionables
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Network Error',
      'Load failed',
      /^Script error\.?$/,
    ],

    beforeSend(event) {
      // Bloquear eventos desde localhost aunque el DSN esté configurado
      if (window.location.hostname === 'localhost') return null
      return event
    },
  })
}
